import { useState, useEffect } from "react";
import { validateCollaborationForm } from "../../validations/collaborationForm";
import { useToastPromise } from "../../components/toastPromise";
import { CollaborationController } from "../../controllers/collaborationController";
import { Textbox } from "../../components/textbox";
import { Checkbox } from "../../components/checkbox";
import { PopupDialog } from "../../components/popupDialog";
import Button from "@mui/material/Button";
import { StorageController } from "../../controllers/storageController";
import { MediaUploaderPreview } from "./mediaUploaderPreview";
import { FileUtil } from "../../util/fileUtil";

export const Collaboration = ({
  user,
  isOpen = false,
  handleClose,
  getAll,
  selectedData,
}) => {
  const initialInputs = {
    title: "",
    licensorLabel: "",
    isOfficialCollab: true,
    startDate: "",
    endDate: "",
    primaryColor: "#ffffff",
    secondaryColor: "#000000",
    tertiaryColor: "#ffffff",
    galleries: null,
    banners: null,
    videos: null,
    menus: null,
    merchandises: null,
    lineUps: null,
    logo: null,
  };
  const toastPromise = useToastPromise();
  const [inputs, setInputs] = useState(initialInputs);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [galleries, setGalleries] = useState([]);
  const [newGalleriesULRs, setNewGalleriesURLs] = useState([]);
  const [currentGalleriesULRs, setCurrentGalleriesURLs] = useState([]);
  const [banners, setBanners] = useState([]);
  const [newBannerULRs, setNewBannerURLs] = useState([]);
  const [currentBannerULRs, setCurrentBannerURLs] = useState([]);
  const [videos, setVideos] = useState([]);
  const [newVideoULRs, setNewVideoURLs] = useState([]);
  const [currentVideoULRs, setCurrentVideoURLs] = useState([]);
  const [menus, setMenus] = useState([]);
  const [newMenuULRs, setNewMenuURLs] = useState([]);
  const [currentMenuULRs, setCurrentMenuURLs] = useState([]);
  const [merchandises, setMerchandises] = useState([]);
  const [newMerchandisesULRs, setNewMerchandisesURLs] = useState([]);
  const [currentMerchandisesULRs, setCurrentMerchandisesURLs] = useState([]);
  const [lineUps, setLineUps] = useState([]);
  const [newLineUpULRs, setNewLineUpURLs] = useState([]);
  const [currentLineUpULRs, setCurrentLineUpURLs] = useState([]);
  const [logo, setLogo] = useState();
  const [newLogoULR, setNewLogoURL] = useState();
  const [currentLogoULR, setCurrentLogoURL] = useState();
  const handleTextChange = (event) => {
    saveInputs(event.target.name, event.target.value);
  };
  const handleCheckboxChange = (event) => {
    saveInputs(event.target.name, event.target.checked);
  };
  const saveInputs = (name, value) => {
    setInputs((values) => ({ ...values, [name]: value }));
  };
  const resetForm = () => {
    setInputs(initialInputs);
    setGalleries([]);
    setBanners([]);
    setVideos([]);
    setMenus([]);
    setMerchandises([]);
    setLineUps([]);
    setLogo();
    setNewGalleriesURLs([]);
    setNewBannerURLs([]);
    setNewVideoURLs([]);
    setNewMenuURLs([]);
    setNewMerchandisesURLs([]);
    setNewLineUpURLs([]);
    setNewLogoURL();
    setCurrentGalleriesURLs([]);
    setCurrentBannerURLs([]);
    setCurrentVideoURLs([]);
    setCurrentMenuURLs([]);
    setCurrentMerchandisesURLs([]);
    setCurrentLineUpURLs([]);
    setCurrentLogoURL();
    setErrors({});
  };
  const handleSubmit = (event) => {
    event.preventDefault();
    const newErrors = validateCollaborationForm(inputs);
    setErrors(newErrors);
    if (Object.keys(newErrors).length !== 0) {
      console.debug("Form submission failed due to validation errors.");
      return;
    }
    console.debug("Form validated successfully.");
    Promise.all([
      uploadImageToFirebase(
        inputs.title.replace(" ", "_") + "_galleries",
        galleries
      ),
      uploadImageToFirebase(
        inputs.title.replace(" ", "_") + "_banners",
        banners
      ),
      uploadImageToFirebase(inputs.title.replace(" ", "_") + "_videos", videos),
      uploadImageToFirebase(inputs.title.replace(" ", "_") + "_menus", menus),
      uploadImageToFirebase(
        inputs.title.replace(" ", "_") + "_merchandises",
        merchandises
      ),
      uploadImageToFirebase(
        inputs.title.replace(" ", "_") + "_lineUps",
        lineUps
      ),
      uploadImageToFirebase(
        inputs.title.replace(" ", "_") + "_logo",
        logo ? [logo] : []
      ),
    ])
      .then(
        ([
          galleriesURLs,
          bannerURLs,
          videoURLs,
          menuURLs,
          merchandiseURLs,
          lineUpsURLs,
          logoURL,
        ]) => {
          setNewGalleriesURLs(galleriesURLs);
          setNewBannerURLs(bannerURLs);
          setNewVideoURLs(videoURLs);
          setNewMenuURLs(menuURLs);
          setNewMerchandisesURLs(merchandiseURLs);
          setNewLineUpURLs(lineUpsURLs);
          setNewLogoURL(logoURL);
          saveCollaboration(
            galleriesURLs,
            bannerURLs,
            videoURLs,
            menuURLs,
            merchandiseURLs,
            lineUpsURLs,
            logoURL
          );
        }
      )
      .finally(() => {
        setLoading(false);
        handleClose();
        resetForm();
        getAll();
      });
  };
  const handleGalleriesChange = async (event) => {
    const files = event.target.files;
    if (files.length < 1) return;
    const selectedGalleries = await FileUtil.toWebpMultiple(Array.from(files));
    setGalleries(selectedGalleries);
    const previewURLs = selectedGalleries.map((file) =>
      URL.createObjectURL(file)
    );
    setNewGalleriesURLs(previewURLs);
    saveInputs(event.target.name, selectedGalleries);
  };
  const handleBannerChange = async (event) => {
    const files = event.target.files;
    if (files.length < 1) return;
    const selectedBanners = await FileUtil.toWebpMultiple(Array.from(files));
    setBanners(selectedBanners);
    const previewURLs = selectedBanners.map((file) =>
      URL.createObjectURL(file)
    );
    setNewBannerURLs(previewURLs);
    saveInputs(event.target.name, selectedBanners);
  };
  const handleVideoChange = (event) => {
    const files = event.target.files;
    const selectedVideos = Array.from(files);
    setVideos(selectedVideos);
    const previewURLs = selectedVideos.map((file) => URL.createObjectURL(file));
    setNewVideoURLs(previewURLs);
    saveInputs(event.target.name, files);
  };
  const handleMenuChange = async (event) => {
    const files = event.target.files;
    if (files.length < 1) return;
    const selectedMenus = await FileUtil.toWebpMultiple(Array.from(files));
    setMenus(selectedMenus);
    const previewURLs = selectedMenus.map((file) => URL.createObjectURL(file));
    setNewMenuURLs(previewURLs);
    saveInputs(event.target.name, selectedMenus);
  };
  const handleMerchandiseChange = async (event) => {
    const files = event.target.files;
    if (files.length < 1) return;
    const selectedMerchandises = await FileUtil.toWebpMultiple(
      Array.from(files)
    );
    setMerchandises(selectedMerchandises);
    const previewURLs = selectedMerchandises.map((file) =>
      URL.createObjectURL(file)
    );
    setNewMerchandisesURLs(previewURLs);
    saveInputs(event.target.name, selectedMerchandises);
  };
  const handleLineUpChange = async (event) => {
    const files = event.target.files;
    if (files.length < 1) return;
    const selectedLineUps = await FileUtil.toWebpMultiple(Array.from(files));
    setLineUps(selectedLineUps);
    const previewURLs = selectedLineUps.map((file) =>
      URL.createObjectURL(file)
    );
    setNewLineUpURLs(previewURLs);
    saveInputs(event.target.name, selectedLineUps);
  };
  const handleLogoChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const selectedLogo = await FileUtil.toWebp(file);
    setLogo(selectedLogo);
    const previewURLs = URL.createObjectURL(selectedLogo);
    setNewLogoURL(previewURLs);
    saveInputs(event.target.name, selectedLogo);
  };
  const uploadImageToFirebase = (folderName, files) => {
    setLoading(true);
    return new Promise((resolve, reject) => {
      StorageController.addAll({
        folderName: folderName,
        files: files,
        resolve: resolve,
        reject: reject,
      });
    });
  };
  const saveCollaboration = (
    galleriesURLs,
    bannerURLs,
    videoURLs,
    menuURLs,
    merchandiseURLs,
    lineUpsURLs,
    logoURL
  ) => {
    let currentGalleries = selectedData?.galleries;
    let currentBanners = selectedData?.banners;
    let currentVideos = selectedData?.videos;
    let currentMenus = selectedData?.menus;
    let currentMerchandises = selectedData?.merchandises;
    let currentLineUps = selectedData?.lineUps;
    let currentLogo = selectedData?.logo;
    toastPromise({
      promise: new Promise((resolve, reject) => {
        selectedData?.id
          ? CollaborationController.update({
              parameters: {
                ...inputs,
                id: selectedData.id,
                galleries: currentGalleries
                  ? currentGalleries.concat(galleriesURLs)
                  : galleriesURLs,
                banners: currentBanners
                  ? currentBanners.concat(bannerURLs)
                  : bannerURLs,
                videos: currentVideos
                  ? currentVideos.concat(videoURLs)
                  : videoURLs,
                menus: currentMenus ? currentMenus.concat(menuURLs) : menuURLs,
                merchandises: currentMerchandises
                  ? currentMerchandises.concat(merchandiseURLs)
                  : merchandiseURLs,
                lineUps: currentLineUps
                  ? currentLineUps.concat(lineUpsURLs)
                  : lineUpsURLs,
                logo: logoURL.length === 1 ? logoURL : currentLogo,
                lastModifiedBy: user.uid,
                lastModifiedDate: new Date().toLocaleString(),
              },
              resolve: resolve,
              reject: reject,
            })
          : CollaborationController.add({
              parameters: {
                ...inputs,
                galleries: galleriesURLs,
                banners: bannerURLs,
                videos: videoURLs,
                menus: menuURLs,
                merchandises: merchandiseURLs,
                lineUps: lineUpsURLs,
                logo: logoURL,
                createdBy: user.uid,
                createdDate: new Date().toLocaleString(),
                lastModifiedBy: user.uid,
                lastModifiedDate: new Date().toLocaleString(),
              },
              resolve: resolve,
              reject: reject,
            });
      }),
      successMessage: (data) => {
        console.debug(data);
        return data;
      },
      errorMessage: (data) => data,
    });
  };
  useEffect(() => {
    if (selectedData) {
      setInputs({
        title: selectedData.title,
        licensorLabel: selectedData.licensorLabel,
        isOfficialCollab: selectedData.isOfficialCollab,
        startDate: selectedData.startDate,
        endDate: selectedData.endDate,
        primaryColor: selectedData.primaryColor,
        secondaryColor: selectedData.secondaryColor,
        tertiaryColor: selectedData.tertiaryColor,
        galleries: selectedData.galleries,
        banners: selectedData.banners,
        videos: selectedData.videos,
        menus: selectedData.menus,
        merchandises: selectedData.merchandises,
        lineUps: selectedData.lineUps,
        logo: selectedData.logo,
      });
      setCurrentGalleriesURLs(selectedData.galleries);
      setCurrentBannerURLs(selectedData.banners);
      setCurrentVideoURLs(selectedData.videos);
      setCurrentMenuURLs(selectedData.menus);
      setCurrentMerchandisesURLs(selectedData.merchandises);
      setCurrentLineUpURLs(selectedData.lineUps);
      setCurrentLogoURL(selectedData.logo);
    }
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  return (
    <PopupDialog
      name="collaboration"
      open={isOpen}
      onClose={() => {
        handleClose();
        resetForm();
      }}
      title={
        selectedData
          ? "Update Collaboration Detail"
          : "Add New Collaboration Detail"
      }
      neutralAction={handleClose}
      positiveAction={
        <Button onClick={handleSubmit} disabled={loading}>
          {loading
            ? selectedData
              ? "Updating"
              : "Adding"
            : selectedData
              ? "Update"
              : "Add"}
        </Button>
      }
      content={
        <form onSubmit={handleSubmit}>
          {selectedData ? (
            <Textbox
              type="text"
              name="id"
              value={selectedData.id}
              placeholder="ID"
              readOnly={true}
            />
          ) : null}
          <Textbox
            type="text"
            name="title"
            placeholder="Title"
            value={inputs.title}
            onChange={handleTextChange}
            errorMsg={errors.title}
          />
          <Textbox
            type="text"
            name="licensorLabel"
            placeholder="Licensor Label"
            value={inputs.licensorLabel}
            onChange={handleTextChange}
            errorMsg={errors.licensorLabel}
          />
          <Checkbox
            name="isOfficialCollab"
            placeholder="Is Official Collab"
            checked={inputs?.isOfficialCollab ?? true}
            onChange={(e) => handleCheckboxChange(e)}
          />
          <Textbox
            type="date"
            name="startDate"
            placeholder="Start Date"
            value={inputs.startDate}
            onChange={handleTextChange}
            errorMsg={errors.startDate}
          />
          <Textbox
            type="date"
            name="endDate"
            placeholder="End Date"
            value={inputs.endDate}
            onChange={handleTextChange}
            errorMsg={errors.endDate}
          />
          <Textbox
            type="color"
            name="primaryColor"
            placeholder="Primary Color"
            value={inputs.primaryColor || "#ffffff"}
            onChange={handleTextChange}
            errorMsg={errors.primaryColor}
          />
          <Textbox
            type="color"
            name="secondaryColor"
            placeholder="Secondary Color"
            value={inputs.secondaryColor || "#000000"}
            onChange={handleTextChange}
            errorMsg={errors.secondaryColor}
          />
          <Textbox
            type="color"
            name="tertiaryColor"
            placeholder="Tertiary Color"
            value={inputs.tertiaryColor || "#ffffff"}
            onChange={handleTextChange}
            errorMsg={errors.tertiaryColor}
          />
          <MediaUploaderPreview
            onChange={handleLogoChange}
            multiple={false}
            name="logo"
            placeholder="Logo"
            errorMsg={errors.logo}
            newList={newLogoULR ? [newLogoULR] : []}
            currentList={currentLogoULR ? [currentLogoULR] : []}
          />
          <MediaUploaderPreview
            onChange={handleGalleriesChange}
            name="galleries"
            placeholder="Galleries"
            errorMsg={errors.galleries}
            newList={newGalleriesULRs}
            currentList={currentGalleriesULRs}
          />
          <MediaUploaderPreview
            onChange={handleBannerChange}
            name="banners"
            placeholder="Banners"
            errorMsg={errors.banners}
            newList={newBannerULRs}
            currentList={currentBannerULRs}
          />
          <MediaUploaderPreview
            type="video"
            accept="video/*"
            onChange={handleVideoChange}
            name="videos"
            placeholder="Videos"
            errorMsg={errors.videos}
            newList={newVideoULRs}
            currentList={currentVideoULRs}
          />
          <MediaUploaderPreview
            onChange={handleMenuChange}
            name="menus"
            placeholder="Menus"
            errorMsg={errors.menus}
            newList={newMenuULRs}
            currentList={currentMenuULRs}
          />
          <MediaUploaderPreview
            onChange={handleMerchandiseChange}
            name="merchandises"
            placeholder="Merchandises"
            errorMsg={errors.merchandises}
            newList={newMerchandisesULRs}
            currentList={currentMerchandisesULRs}
          />
          <MediaUploaderPreview
            onChange={handleLineUpChange}
            name="lineUps"
            placeholder="Line Ups"
            errorMsg={errors.lineUps}
            newList={newLineUpULRs}
            currentList={currentLineUpULRs}
          />
        </form>
      }
    />
  );
};
